/**
 * Line icons, mostly for the composer toolbar.
 *
 * Drawn rather than pulled from an icon package: there are a handful of them,
 * and a dependency would ship a few thousand glyphs to render a paperclip. They
 * share
 * one stroke weight and inherit `currentColor`, so they sit at the same visual
 * weight as the lettered buttons (B, I, U) beside them and take the toolbar's
 * grey without any per-icon styling.
 */

interface IconProps {
  className?: string;
}

function Svg({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      {children}
    </svg>
  );
}

/** A magnifier, for the ticket search box. */
export function SearchIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8 20 20" />
    </Svg>
  );
}

/** Two chain links, for insert-link. */
export function LinkIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M9.5 14.5 14.5 9.5" />
      <path d="M11 6.5 12.8 4.7a4 4 0 0 1 5.7 5.7l-1.8 1.8" />
      <path d="M13 17.5l-1.8 1.8a4 4 0 0 1-5.7-5.7l1.8-1.8" />
    </Svg>
  );
}

/** A face, for the emoji picker. */
export function SmileyIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 14.2a4.2 4.2 0 0 0 7 0" />
      <path d="M9.3 9.6h.01" />
      <path d="M14.7 9.6h.01" />
    </Svg>
  );
}

/** A paperclip, for attachments. */
export function PaperclipIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M18.5 11.3 12 17.8a4.6 4.6 0 0 1-6.5-6.5l7-7a3.1 3.1 0 0 1 4.4 4.4l-7 7a1.5 1.5 0 0 1-2.2-2.2l6.4-6.4" />
    </Svg>
  );
}

/**
 * A pencil, for canned responses.
 *
 * The diagonal body reads clearly against the paperclip's curve at 15px, which
 * is the only thing that matters when two icon-only buttons sit side by side.
 */
export function PencilIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M4 20h4.2L19.1 9.1a2.3 2.3 0 0 0-3.2-3.2L5 16.8z" />
      <path d="M14.6 7.2 17.8 10.4" />
    </Svg>
  );
}

/**
 * An envelope, for a ticket that arrived as email.
 *
 * The flap is a separate stroke rather than a triangle inside the rectangle:
 * at 14px a filled or closed flap merges with the top edge and the whole thing
 * reads as a plain box.
 */
export function MailIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="m3.5 7.5 7.4 5.2a2 2 0 0 0 2.2 0l7.4-5.2" />
    </Svg>
  );
}

/**
 * WhatsApp's mark, for a ticket that arrived on the phone number.
 *
 * Filled rather than stroked, and the only icon in this file that is. The
 * others sit in a toolbar beside lettered buttons and match their weight; this
 * one sits in a queue at 14px next to an envelope, where the single thing that
 * matters is telling the two apart at a glance down a column of eighty rows.
 * A stroked bubble with a stroked handset inside it is two nested outlines in
 * six pixels, and at that size it is a smudge.
 *
 * So: the silhouette does the work -- a round bubble with the tail bottom-left,
 * which is the shape people recognise even in grey -- and the handset is a hole
 * through it rather than a shape on top, which is how the real mark is drawn
 * and the only way it survives on any background.
 */
export function WhatsAppIcon({ className }: IconProps) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className={className}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2.2a9.6 9.6 0 0 0-8.1 14.7l-1.6 4.9 5.1-1.6A9.6 9.6 0 1 0 12 2.2Zm-3.2 5c.2 0 .4 0 .5.4l.9 2.1c.1.2 0 .4-.1.5l-.6.7c-.2.2-.3.4-.1.7.5.9 1.1 1.6 1.9 2.2.6.5 1.2.8 1.5.9.3.1.5.1.7-.1l.8-1c.2-.2.3-.2.6-.1l2 1c.3.1.4.3.4.5 0 .6-.3 1.2-.8 1.6-.5.4-1.1.6-1.7.6-1.1 0-2.7-.6-4.4-2-1.7-1.4-2.9-3-3.4-4.2-.3-.7-.4-1.3-.3-1.9.1-.6.4-1.1.9-1.5.3-.3.6-.4 1-.4h.2Z"
      />
    </svg>
  );
}

/**
 * A globe, for a ticket that arrived through a form on the website.
 *
 * Stroked and plain, like the envelope beside it: the point of this column is
 * that a row which is *not* email stands out, not that anybody admires the
 * glyph. Two meridians rather than a full wireframe -- at 14px a third one
 * closes the gaps and the whole thing reads as a filled circle.
 */
export function GlobeIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18Z" />
    </Svg>
  );
}

/**
 * A shopping bag, for a ticket that is a conversation with an eBay member.
 *
 * A bag rather than eBay's mark, for the reason every glyph in the channel
 * column is plain and grey: how a ticket arrived is context, and a brand's
 * colours in a queue would outrank the clock. It only has to say "the
 * marketplace" at 14px, and a bag with a handle does.
 */
export function MarketplaceIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M5 8h14l-1 12H6L5 8Z" />
      <path d="M9 8V6.5a3 3 0 0 1 6 0V8" />
    </Svg>
  );
}

/** A speech bubble, for a ticket that arrived by SMS. */
export function MessageIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M21 11.5a7.5 7.5 0 0 1-8 7.5c-1 0-2-.2-2.9-.5L5 20l1.4-4.2A7.5 7.5 0 1 1 21 11.5Z" />
    </Svg>
  );
}
